import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  Phone,
  User as UserIcon,
  Package,
  Loader2,
  Camera,
  ZoomIn,
} from "lucide-react";
import { useDrafts } from "../../contexts/DraftsContext";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// Arabic-aware normalize: collapse common letter variants so users don't have
// to type the exact diacritical form. Examples:
//   "أحمد" == "احمد" == "إحمد", "مدرسة" == "مدرسه", "موسى" == "موسي"
const normAr = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[\u064B-\u0652]/g, "") // tashkeel
    .replace(/[إأآا]/g, "ا")
    .replace(/[ىي]/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/ؤ/g, "و")
    .replace(/ئ/g, "ي")
    .replace(/\s+/g, " ")
    .trim();

// Smart, multi-word, order-insensitive matcher. All words in `query` must
// appear (in any order) somewhere in the searchable `text`. Arabic letter
// variants are normalized for both sides.
const smartMatch = (text, query) => {
  const q = normAr(query);
  if (!q) return true;
  const hay = normAr(text);
  const terms = q.split(" ").filter(Boolean);
  return terms.every((t) => hay.includes(t));
};

// Build the searchable haystack for a product (name, weight, carton, category, brand, price)
const productHaystack = (p) =>
  `${p.name_ar || ""} ${p.name_en || ""} ${p.weight_per_piece || ""} ${p.units_per_carton || ""} ${p.category || ""} ${p.brand_name || ""} ${p.cost_price || ""}`;

const waLink = (phone) => {
  const p = (phone || "").replace(/[^0-9]/g, "");
  if (!p) return null;
  const intl = p.startsWith("0") ? `964${p.slice(1)}` : p;
  return `https://wa.me/${intl}`;
};

// ─── Image lightbox ───
const Lightbox = ({ src, onClose }) =>
  !src ? null : (
    <div className="fixed inset-0 z-[200] bg-black/85 flex items-center justify-center p-4" onClick={onClose} data-testid="lightbox">
      <img src={src} alt="" className="max-h-[90vh] max-w-[95vw] object-contain rounded-2xl" />
      <button onClick={onClose} className="absolute top-4 right-4 bg-white/15 hover:bg-white/30 rounded-full p-2"><X className="w-5 h-5 text-white" /></button>
    </div>
  );

// ─── Product picker modal ───
const ProductPicker = ({ allProducts, selectedIds, onClose, onConfirm }) => {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(new Set(selectedIds || []));
  const list = useMemo(() => {
    if (!q.trim()) return allProducts;
    return allProducts.filter((p) => smartMatch(productHaystack(p), q));
  }, [q, allProducts]);
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <div className="fixed inset-0 z-[155] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="product-picker" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#1E3A5F] text-white p-3 flex justify-between items-center">
          <h4 className="font-bold flex items-center gap-1"><Package className="w-4 h-4" /> اختيار المواد ({picked.size})</h4>
          <button onClick={onClose} className="bg-white/20 hover:bg-white/30 rounded-full p-1.5"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="بحث ذكي... مثال: دجاج بوادي 1000"
              data-testid="product-picker-search"
              className="w-full pr-10 pl-10 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#3B82F6] text-sm" />
            {q && (
              <button type="button" onClick={() => setQ("")}
                data-testid="product-picker-search-clear"
                title="مسح البحث"
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#FEE2E2] hover:bg-[#FECACA] text-[#E85D45] rounded-full w-6 h-6 flex items-center justify-center">
                <X className="w-3.5 h-3.5" strokeWidth={3} />
              </button>
            )}
          </div>
          {q && (
            <p className="text-[10px] text-[#5C6E64] mt-1.5 px-1" dir="ltr">
              {list.length} / {allProducts.length} مادة مطابقة
            </p>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {list.length === 0 ? (
            <p className="col-span-full text-center text-[#5C6E64] text-sm py-8" data-testid="product-picker-no-match">
              لا توجد مواد تطابق "<span className="font-bold text-[#1E3A5F]">{q}</span>"
            </p>
          ) : list.map((p) => {
            const isOn = picked.has(p.id);
            return (
              <button key={p.id} type="button" onClick={() => toggle(p.id)} data-testid={`pp-pick-${p.id}`}
                className={`text-right rounded-xl p-2 border-2 transition-colors ${isOn ? "border-[#15803d] bg-[#ECFDF5]" : "border-[#E4EBE6] hover:border-[#3B82F6]"}`}>
                {p.image_path ? (
                  <img src={`${API}/files/${p.image_path}`} alt={p.name_ar} className="w-full h-20 object-cover rounded-lg mb-1" />
                ) : <div className="w-full h-20 bg-[#F3F6F4] rounded-lg flex items-center justify-center"><Package className="w-6 h-6 text-[#5C6E64]" /></div>}
                <p className="text-xs font-bold truncate">{p.name_ar}</p>
                <p className="text-[10px] text-[#5C6E64]" dir="ltr">سعر شراء: {fmt(p.cost_price)} د.ع</p>
              </button>
            );
          })}
        </div>
        <div className="p-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="bg-[#F3F6F4] text-[#5C6E64] py-2 px-4 rounded-full text-sm font-bold">إلغاء</button>
          <button onClick={() => onConfirm([...picked])} data-testid="product-picker-confirm"
            className="bg-[#7ED321] hover:bg-[#6cb91d] text-white py-2 px-5 rounded-full text-sm font-bold">تأكيد ({picked.size})</button>
        </div>
      </div>
    </div>
  );
};

// ─── Company Form Modal ───
const CompanyFormModal = ({ company, allProducts, onClose, onSaved }) => {
  const isEdit = !!company?.id;
  const stash = useRef(useDrafts().loadDraft("pc-form") || null).current;
  const [name, setName] = useState(company?.name || stash?.name || "");
  const [phone, setPhone] = useState(company?.sales_phone || stash?.phone || "");
  const [owner, setOwner] = useState(company?.owner_name || stash?.owner || "");
  const [imagePath, setImagePath] = useState(company?.image_path || stash?.imagePath || null);
  const [productIds, setProductIds] = useState(company?.product_ids || stash?.productIds || []);
  const [showPicker, setShowPicker] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const { requestClose, saveDraft, clearDirty } = useDrafts();

  const hasContent = !isEdit && (name.trim() || phone.trim() || owner.trim() || imagePath || productIds.length > 0);
  useEffect(() => {
    if (!isEdit && hasContent) saveDraft("pc-form", "إضافة شركة شراء", { name, phone, owner, imagePath, productIds });
  }, [name, phone, owner, imagePath, productIds, hasContent, isEdit, saveDraft]);

  const uploadImage = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload-vehicle-photo`, fd);
      setImagePath(data.path);
      toast.success("تم رفع الصورة");
    } catch (e) { toast.error("فشل رفع الصورة"); }
  };

  const guardedClose = () => requestClose(hasContent, isEdit ? "تعديل شركة" : "إضافة شركة شراء", onClose);

  const submit = async () => {
    if (!name.trim()) { toast.error("اكتب اسم الشركة"); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), sales_phone: phone.trim(), owner_name: owner.trim(), image_path: imagePath, product_ids: productIds };
      if (isEdit) await axios.patch(`${API}/admin/purchase-companies/${company.id}`, payload);
      else await axios.post(`${API}/admin/purchase-companies`, payload);
      toast.success(isEdit ? "تم التحديث" : "تم إضافة الشركة");
      clearDirty("pc-form");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل الحفظ"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[150] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="pc-form-modal" onClick={guardedClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[94vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white p-4 flex justify-between items-center shrink-0">
          <h3 className="font-bold text-lg flex items-center gap-2"><Building2 className="w-5 h-5" /> {isEdit ? "تعديل شركة" : "إضافة شركة شراء"}</h3>
          <button onClick={guardedClose} data-testid="pc-form-close" className="bg-white/20 hover:bg-white/30 rounded-full p-1.5"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {/* Image picker */}
          <div className="text-center">
            <p className="text-xs font-bold text-[#5C6E64] mb-2">صورة الشركة</p>
            <div className="flex gap-2 justify-center">
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="pc-upload-image"
                className="w-28 h-28 rounded-2xl border-2 border-dashed border-[#3B82F6] bg-[#F8FAFC] flex items-center justify-center overflow-hidden">
                {imagePath ? (
                  <img src={`${API}/files/${imagePath}`} alt="" className="w-full h-full object-cover" />
                ) : <Camera className="w-7 h-7 text-[#3B82F6]" />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
              <button type="button" onClick={() => setShowImagePicker(true)} data-testid="pc-pick-product-img"
                className="bg-[#7ED321] hover:bg-[#6cb91d] text-white text-xs font-bold px-3 rounded-xl">
                من المواد الموجودة
              </button>
            </div>
            {imagePath && <button onClick={() => setImagePath(null)} className="text-[10px] text-[#E85D45] mt-1 hover:underline">حذف الصورة</button>}
          </div>
          <div>
            <label className="text-xs font-bold text-[#5C6E64] block mb-1">اسم الشركة *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="pc-form-name"
              className="w-full px-3 py-2.5 rounded-xl border-2 border-[#3B82F6] focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold text-[#5C6E64] block mb-1">رقم المبيعات</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="pc-form-phone" dir="ltr"
                placeholder="07700000000" className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#3B82F6]" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#5C6E64] block mb-1">اسم صاحب المبيعات</label>
              <input value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="pc-form-owner"
                className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#3B82F6]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-[#5C6E64] block mb-1">المواد المرتبطة ({productIds.length})</label>
            <button type="button" onClick={() => setShowPicker(true)} data-testid="pc-open-product-picker"
              className="w-full bg-[#EFF6FF] hover:bg-[#DBEAFE] border-2 border-[#3B82F6] text-[#1E3A5F] font-bold py-2 rounded-xl flex items-center justify-center gap-1 text-sm">
              <Package className="w-4 h-4" /> اختر مواد من الإدارة الرئيسية
            </button>
          </div>
        </div>
        <div className="p-3 border-t-2 border-[#E4EBE6] flex justify-end gap-2 shrink-0">
          <button onClick={guardedClose} className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2 px-4 rounded-full">إلغاء</button>
          <button onClick={submit} disabled={saving} data-testid="pc-form-submit"
            className="bg-[#7ED321] hover:bg-[#6cb91d] disabled:bg-[#BAE6FD] text-white font-bold py-2 px-5 rounded-full flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {isEdit ? "حفظ" : "إضافة"}
          </button>
        </div>
      </div>
      {showPicker && <ProductPicker allProducts={allProducts} selectedIds={productIds} onClose={() => setShowPicker(false)} onConfirm={(ids) => { setProductIds(ids); setShowPicker(false); }} />}
      {showImagePicker && (
        <ProductPicker allProducts={allProducts} selectedIds={[]} onClose={() => setShowImagePicker(false)}
          onConfirm={(ids) => {
            const first = allProducts.find((p) => ids.includes(p.id));
            if (first?.image_path) { setImagePath(first.image_path); toast.success("تم اختيار الصورة"); }
            setShowImagePicker(false);
          }} />
      )}
    </div>
  );
};

// Inline editable row for "weight_per_piece" / "units_per_carton" inside the
// detail card. Click to edit, Enter (or blur) to save, Esc to cancel.
const InlineEditRow = ({ label, value, kind, saving, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => { setDraft(value ?? ""); }, [value]);

  const commit = () => {
    setEditing(false);
    const cur = (value ?? "").toString();
    const next = (draft ?? "").toString();
    if (cur !== next) onSave(next);
  };
  const cancel = () => { setDraft(value ?? ""); setEditing(false); };

  const display = value === null || value === undefined || value === "" ? "—" : value;
  return (
    <div className="flex justify-between items-center py-1.5">
      <span className="font-bold text-[#1E3A5F]">{label}</span>
      {editing ? (
        <div className="flex items-center gap-1 flex-1 max-w-[140px] ms-2">
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); else if (e.key === "Escape") cancel(); }}
            onBlur={commit}
            inputMode={kind === "number" ? "numeric" : "text"}
            placeholder={kind === "number" ? "10" : "900غ"}
            data-testid={`inline-input-${kind}`}
            className="w-full px-2 py-1 rounded border-2 border-[#3B82F6] focus:outline-none text-sm font-bold text-[#1E3A5F]"
            dir={kind === "number" ? "ltr" : "rtl"}
          />
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#3B82F6]" />}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid={`inline-edit-${kind}`}
          title="انقر للتعديل"
          className="font-black text-[#0B2A55] text-base hover:bg-white/60 rounded px-2 py-0.5 flex items-center gap-1 transition-colors"
          dir={kind === "number" ? "ltr" : undefined}
        >
          <span>{display}</span>
          <Edit2 className="w-3.5 h-3.5 text-[#3B82F6] opacity-70" />
        </button>
      )}
    </div>
  );
};

// ─── Company Detail Modal (products gallery) ───
const CompanyDetail = ({ company, allProducts, onClose, onChanged }) => {
  const [zoom, setZoom] = useState(null);
  const [productIds, setProductIds] = useState(company.product_ids || []);
  const [removing, setRemoving] = useState(null);
  const [q, setQ] = useState("");
  // Local override map so the displayed values update instantly after a save,
  // without waiting for the parent's full reload. Shape: { [productId]: { weight_per_piece, units_per_carton } }
  const [overrides, setOverrides] = useState({});
  const [savingField, setSavingField] = useState(null); // e.g. "abc-weight" | "abc-carton"

  // CRITICAL: Re-sync when the company object updates (e.g. parent reloaded
  // after edit/save with newly added products). Without this, productIds is
  // stuck on the FIRST snapshot and the new products never appear.
  useEffect(() => {
    setProductIds(company.product_ids || []);
  }, [company.id, company.product_ids]);
  const products = useMemo(() => {
    const idset = new Set(productIds);
    return allProducts.filter((p) => idset.has(p.id));
  }, [productIds, allProducts]);

  const filtered = useMemo(() => {
    if (!q.trim()) return products;
    return products.filter((p) => smartMatch(productHaystack(p), q));
  }, [products, q]);

  const removeOne = async (p) => {
    if (!(await confirmDialog(`إزالة "${p.name_ar}" من مواد شركة ${company.name}؟`))) return;
    const next = productIds.filter((id) => id !== p.id);
    setRemoving(p.id);
    try {
      await axios.patch(`${API}/admin/purchase-companies/${company.id}`, { product_ids: next });
      setProductIds(next);
      toast.success("تم حذف المادة من الشركة");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحذف");
    } finally {
      setRemoving(null);
    }
  };

  // Bulk actions (Feb 17, 2026 request) — quickly hide EVERY product of
  // this company from the offers section, or mark them all "no discount".
  // Iterates PATCH /admin/products/{id} client-side; simple and reuses the
  // existing per-product endpoint.
  const [bulkBusy, setBulkBusy] = useState(null); // "offers" | "rewards"
  const bulkToggle = async (field, target) => {
    const key = field === "is_offer" ? "offers" : "rewards";
    const label = field === "is_offer" ? "إخفاء العروض" : "إلغاء الخصم";
    const affected = products.filter((p) => Boolean(p[field]) !== target);
    if (affected.length === 0) {
      toast.info(`لا توجد مواد لتعديلها — كلهم بالفعل ${target ? "مفعّلين" : "مطفيين"}`);
      return;
    }
    if (!(await confirmDialog(
      `${label} — سيتم تحديث ${affected.length} مادة من ${products.length}. متابعة؟`
    ))) return;
    setBulkBusy(key);
    let ok = 0, fail = 0;
    for (const p of affected) {
      try {
        const payload = { [field]: target };
        if (field === "is_offer" && target === false) payload.featured_offer_slot = null;
        await axios.patch(`${API}/admin/products/${p.id}`, payload);
        ok++;
      } catch { fail++; }
    }
    toast.success(`تم تحديث ${ok} مادة${fail ? ` · فشل ${fail}` : ""}`);
    onChanged && onChanged();
    setBulkBusy(null);
  };

  // Inline-save a product field (weight_per_piece OR units_per_carton OR stock).
  // Persists to the main inventory so the change shows in main admin too.
  const saveProductField = async (productId, field, value) => {
    const key = `${productId}-${field}`;
    setSavingField(key);
    try {
      let payload;
      if (field === "units_per_carton" || field === "stock") {
        payload = { [field]: value === "" || value == null ? null : Number(value) };
      } else {
        payload = { [field]: (value || "").toString().trim() || null };
      }
      await axios.patch(`${API}/admin/products/${productId}`, payload);
      setOverrides((m) => ({ ...m, [productId]: { ...(m[productId] || {}), [field]: payload[field] } }));
      toast.success("تم الحفظ");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally {
      setSavingField(null);
    }
  };

  // Quick per-product toggle for `is_offer` or `excludes_rewards` — lets
  // admin flick a product in/out of offers or add/remove the "discount"
  // flag without opening the full edit modal. Feb 17, 2026 request.
  const toggleField = async (p, field) => {
    const nextVal = !p[field];
    const payload = { [field]: nextVal };
    if (field === "is_offer" && !nextVal) payload.featured_offer_slot = null;
    setSavingField(`${p.id}-${field}`);
    try {
      await axios.patch(`${API}/admin/products/${p.id}`, payload);
      setOverrides((m) => ({ ...m, [p.id]: { ...(m[p.id] || {}), [field]: nextVal } }));
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التحديث");
    } finally {
      setSavingField(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[140] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="pc-detail" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[94vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white p-4 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            {company.image_path && <img src={`${API}/files/${company.image_path}`} alt={company.name} className="w-12 h-12 rounded-xl object-cover border-2 border-white cursor-zoom-in" onClick={() => setZoom(`${API}/files/${company.image_path}`)} />}
            <div>
              <h3 className="font-bold text-lg">{company.name}</h3>
              <p className="text-[11px] opacity-90">{company.owner_name || "—"} · {fmt(products.length)} مادة</p>
            </div>
          </div>
          <button onClick={onClose} className="bg-white/20 hover:bg-white/30 rounded-full p-1.5"><X className="w-5 h-5" /></button>
        </div>
        {/* Inline search bar */}
        <div className="px-3 py-2 border-b border-[#E4EBE6] bg-[#F8FAFC] shrink-0">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="pc-detail-search"
              placeholder="بحث سريع... مثال: دجاج 1000"
              className="w-full pr-10 pl-10 py-2.5 rounded-full border-2 border-[#E4EBE6] bg-white focus:outline-none focus:border-[#3B82F6] text-sm"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                data-testid="pc-detail-search-clear"
                title="مسح البحث"
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#FEE2E2] hover:bg-[#FECACA] text-[#E85D45] rounded-full w-6 h-6 flex items-center justify-center"
              >
                <X className="w-3.5 h-3.5" strokeWidth={3} />
              </button>
            )}
          </div>
          {q && (
            <p className="text-[10px] text-[#5C6E64] mt-1.5 px-2" dir="ltr">
              {filtered.length} / {products.length} مادة مطابقة
            </p>
          )}
          {/* Bulk actions — hide ALL from offers / mark ALL "no discount" */}
          <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid="pc-bulk-actions">
            <button
              onClick={() => bulkToggle("is_offer", false)}
              disabled={bulkBusy === "offers"}
              data-testid="pc-bulk-hide-offers"
              className="bg-[#FEE2E2] hover:bg-[#FECACA] disabled:opacity-50 text-[#B91C1C] text-[11px] font-black rounded-full px-2 py-1.5 flex items-center justify-center gap-1 border border-[#E85D45]/30"
              title="إزالة كل مواد هذه الشركة من صفحة العروض"
            >
              {bulkBusy === "offers" ? <Loader2 className="w-3 h-3 animate-spin" /> : "🚫"}
              إخفاء كل العروض
            </button>
            <button
              onClick={() => bulkToggle("excludes_rewards", true)}
              disabled={bulkBusy === "rewards"}
              data-testid="pc-bulk-no-discount"
              className="bg-[#E0F2FE] hover:bg-[#BAE6FD] disabled:opacity-50 text-[#0369A1] text-[11px] font-black rounded-full px-2 py-1.5 flex items-center justify-center gap-1 border border-[#0EA5E9]/30"
              title="إيقاف مكافأة 150 د.ع/قطعة لكل مواد الشركة"
            >
              {bulkBusy === "rewards" ? <Loader2 className="w-3 h-3 animate-spin" /> : "🎁"}
              إلغاء الخصم لكل المواد
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {products.length === 0 ? (
            <p className="col-span-full text-center text-[#5C6E64] text-sm py-8">لا توجد مواد مرتبطة بهذه الشركة بعد. عدّل الشركة وأضف مواد.</p>
          ) : filtered.length === 0 ? (
            <p className="col-span-full text-center text-[#5C6E64] text-sm py-8" data-testid="pc-detail-no-match">
              لا توجد مواد تطابق "<span className="font-bold text-[#1E3A5F]">{q}</span>"
            </p>
          ) : filtered.map((p) => (
            <div key={p.id} data-testid={`pc-product-${p.id}`}
              className="bg-white rounded-2xl border-2 border-[#E4EBE6] overflow-hidden shadow-sm relative flex flex-col min-h-[520px]">
              {/* X delete button on top */}
              <button
                type="button"
                onClick={() => removeOne(p)}
                disabled={removing === p.id}
                data-testid={`pc-product-remove-${p.id}`}
                title="إزالة من الشركة"
                className="absolute top-2 left-2 z-10 bg-[#E85D45] hover:bg-[#d54d35] disabled:bg-[#FCA5A5] text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg border-2 border-white"
              >
                {removing === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" strokeWidth={3} />}
              </button>
              {/* Title bar — wrap to 2 lines, larger font */}
              <div className="bg-[#1E3A5F] text-white text-sm font-bold p-3 pl-12 leading-snug min-h-[52px] flex items-center shrink-0">{p.name_ar || "(بدون اسم)"}</div>
              {/* Full product image — object-contain so nothing is cropped. ALWAYS renders an h-56 box even on broken/missing image. */}
              <div className="relative w-full h-56 bg-[#F8FAFC] flex items-center justify-center border-b border-[#E4EBE6] shrink-0">
                {p.image_path ? (
                  <>
                    <img
                      src={`${API}/files/${p.image_path}`}
                      alt={p.name_ar || ""}
                      className="w-full h-full object-contain p-2 cursor-zoom-in"
                      onClick={() => setZoom(`${API}/files/${p.image_path}`)}
                      onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = "flex"); }}
                    />
                    <div className="absolute inset-0 hidden items-center justify-center bg-[#F3F6F4]">
                      <Package className="w-12 h-12 text-[#5C6E64]" />
                    </div>
                    <ZoomIn className="absolute bottom-1 left-1 w-5 h-5 text-white bg-black/50 rounded p-0.5 pointer-events-none" />
                  </>
                ) : (
                  <Package className="w-12 h-12 text-[#5C6E64]" />
                )}
              </div>
              {/* Details — ALWAYS render 4 rows with distinct colored accents */}
              <div className="p-3 text-sm space-y-2 bg-[#FAFCFA] flex-1">
                <p className="flex justify-between items-center bg-[#ECFDF5] rounded-lg px-2 py-1.5 border-2 border-[#15803d]/40">
                  <span className="text-[#15803d] font-bold flex items-center gap-1">💰 سعر الشراء</span>
                  <span className="font-black text-[#15803d] text-base" dir="ltr">{p.cost_price ? `${fmt(p.cost_price)} د.ع` : "—"}</span>
                </p>
                <div className="bg-[#EFF6FF] rounded-lg px-2 py-0 border-2 border-[#3B82F6]/40">
                  <InlineEditRow
                    label="⚖️ وزن القطعة"
                    value={(overrides[p.id]?.weight_per_piece !== undefined ? overrides[p.id].weight_per_piece : p.weight_per_piece) || ""}
                    kind="weight"
                    saving={savingField === `${p.id}-weight_per_piece`}
                    onSave={(v) => saveProductField(p.id, "weight_per_piece", v)}
                  />
                </div>
                <div className="bg-[#FFFBEB] rounded-lg px-2 py-0 border-2 border-[#F59E0B]/40">
                  <InlineEditRow
                    label="📦 عدد بالكرتون"
                    value={(overrides[p.id]?.units_per_carton !== undefined ? overrides[p.id].units_per_carton : p.units_per_carton) ?? ""}
                    kind="number"
                    saving={savingField === `${p.id}-units_per_carton`}
                    onSave={(v) => saveProductField(p.id, "units_per_carton", v)}
                  />
                </div>
                <div className="bg-[#FAF5FF] rounded-lg px-2 py-0 border-2 border-[#7C3AED]/40">
                  <InlineEditRow
                    label="🏬 الموجود بالمخزن"
                    value={(overrides[p.id]?.stock !== undefined ? overrides[p.id].stock : p.stock) ?? ""}
                    kind="number"
                    saving={savingField === `${p.id}-stock`}
                    onSave={(v) => saveProductField(p.id, "stock", v)}
                  />
                </div>
                {/* Quick toggles: in offers ↔ off · has-discount ↔ no-discount */}
                {(() => {
                  const isOff = overrides[p.id]?.is_offer !== undefined ? overrides[p.id].is_offer : p.is_offer;
                  const noDisc = overrides[p.id]?.excludes_rewards !== undefined ? overrides[p.id].excludes_rewards : p.excludes_rewards;
                  return (
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        onClick={() => toggleField(p, "is_offer")}
                        disabled={savingField === `${p.id}-is_offer`}
                        data-testid={`pc-toggle-offer-${p.id}`}
                        className={`text-[10px] font-black rounded-lg px-2 py-1.5 flex items-center justify-center gap-1 border-2 transition-colors ${
                          isOff
                            ? "bg-[#DCFCE7] hover:bg-[#BBF7D0] text-[#15803D] border-[#15803D]/40"
                            : "bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#6B7280] border-[#D1D5DB]"
                        }`}
                      >
                        {savingField === `${p.id}-is_offer` ? <Loader2 className="w-3 h-3 animate-spin" /> : (isOff ? "✓" : "◯")}
                        {isOff ? "بالعروض" : "غير معروض"}
                      </button>
                      <button
                        onClick={() => toggleField(p, "excludes_rewards")}
                        disabled={savingField === `${p.id}-excludes_rewards`}
                        data-testid={`pc-toggle-nodisc-${p.id}`}
                        className={`text-[10px] font-black rounded-lg px-2 py-1.5 flex items-center justify-center gap-1 border-2 transition-colors ${
                          noDisc
                            ? "bg-[#FEE2E2] hover:bg-[#FECACA] text-[#B91C1C] border-[#E85D45]/40"
                            : "bg-[#FFF8EC] hover:bg-[#FED7AA] text-[#a06e10] border-[#F59E0B]/40"
                        }`}
                      >
                        {savingField === `${p.id}-excludes_rewards` ? <Loader2 className="w-3 h-3 animate-spin" /> : (noDisc ? "🚫" : "🎁")}
                        {noDisc ? "بلا خصم" : "عليها خصم"}
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Lightbox src={zoom} onClose={() => setZoom(null)} />
    </div>
  );
};

// ─── Main tab ───
const PurchaseCompaniesTab = () => {
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // "new" | company obj | null
  const [viewing, setViewing] = useState(null);
  const [zoom, setZoom] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/purchase-companies`),
        axios.get(`${API}/admin/products`),
      ]);
      setCompanies(r1.data || []);
      setProducts(r2.data || []);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Listen for "resume draft" events from the bell dropdown — auto-open the
  // add-company form so the user lands directly back on the draft.
  useEffect(() => {
    const onResume = (e) => {
      if (e.detail?.draftId === "pc-form" && !editing) setEditing("new");
    };
    window.addEventListener("hr-resume-draft", onResume);
    return () => window.removeEventListener("hr-resume-draft", onResume);
  }, [editing]);

  // When companies list refreshes after add/edit/delete, re-sync `viewing` to
  // point at the freshest data from the new array (otherwise the detail modal
  // keeps stale product_ids and the new products never appear).
  useEffect(() => {
    if (!viewing) return;
    const fresh = companies.find((c) => c.id === viewing.id);
    if (fresh && fresh !== viewing) setViewing(fresh);
  }, [companies]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return companies;
    return companies.filter((c) => (c.name || "").toLowerCase().includes(t) || (c.owner_name || "").toLowerCase().includes(t));
  }, [companies, q]);

  const removeCompany = async (c) => {
    if (!(await confirmDialog(`حذف شركة "${c.name}"؟`))) return;
    try { await axios.delete(`${API}/admin/purchase-companies/${c.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "فشل"); }
  };

  return (
    <div className="space-y-3" data-testid="purchase-companies-tab" dir="rtl">
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1"><Building2 className="w-5 h-5" /><h3 className="font-bold text-lg">شركات الشراء</h3></div>
        <p className="text-xs opacity-90">دليل شركات المُجهّزين مع رقم مبيعات قابل للنقر للواتساب + مواد كل شركة.</p>
        <p className="text-[11px] opacity-80 mt-1" dir="ltr">عدد الشركات: <span className="font-bold">{fmt(companies.length)}</span></p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث عن اسم الشركة..." data-testid="pc-search"
            className="w-full pr-10 pl-4 py-2.5 rounded-full border-2 border-[#E4EBE6] bg-white focus:outline-none focus:border-[#3B82F6]" />
        </div>
        <button onClick={() => setEditing("new")} data-testid="pc-add-btn"
          className="bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2.5 px-4 rounded-full flex items-center gap-1">
          <Plus className="w-4 h-4" /> إضافة شركة
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin inline text-[#1E3A5F]" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-[#5C6E64] py-12 bg-white rounded-2xl border-2 border-dashed border-[#E4EBE6]">
          <Building2 className="w-10 h-10 mx-auto mb-2 opacity-60" />
          <p className="text-sm">لا توجد شركات. أضف أول شركة شراء.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((c) => {
            const wa = waLink(c.sales_phone);
            return (
              <div key={c.id} data-testid={`pc-card-${c.id}`} className="bg-white rounded-2xl border-2 border-[#E4EBE6] overflow-hidden shadow-sm">
                <div className="bg-[#1E3A5F] text-white py-1.5 px-3 flex items-center justify-between">
                  <p className="font-bold text-sm truncate">{c.name}</p>
                  <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full" dir="ltr">{fmt((c.product_ids || []).length)} مادة</span>
                </div>
                <div className="p-3 flex items-start gap-3">
                  <button type="button" onClick={() => c.image_path && setZoom(`${API}/files/${c.image_path}`)}
                    onDoubleClick={() => setViewing(c)} className="shrink-0">
                    {c.image_path ? (
                      <img src={`${API}/files/${c.image_path}`} alt={c.name} className="w-20 h-20 rounded-2xl object-cover border-2 border-[#3B82F6]" />
                    ) : <div className="w-20 h-20 rounded-2xl bg-[#F0F9FF] border-2 border-[#E4EBE6] flex items-center justify-center"><Building2 className="w-7 h-7 text-[#3B82F6]" /></div>}
                  </button>
                  <div className="flex-1 min-w-0 space-y-1">
                    {wa ? (
                      <a href={wa} target="_blank" rel="noreferrer" data-testid={`pc-wa-${c.id}`}
                        className="flex items-center gap-1 text-[#25D366] hover:underline font-bold text-sm">
                        <Phone className="w-3.5 h-3.5" /> <span dir="ltr">{c.sales_phone}</span>
                      </a>
                    ) : <p className="text-xs text-[#5C6E64]">لا يوجد رقم مبيعات</p>}
                    {c.owner_name && <p className="text-xs flex items-center gap-1"><UserIcon className="w-3 h-3 text-[#5C6E64]" /> {c.owner_name}</p>}
                    <button onClick={() => setViewing(c)} data-testid={`pc-view-${c.id}`}
                      className="text-[11px] bg-[#1E3A5F] hover:bg-[#152a45] text-white font-bold py-1.5 px-3 rounded-full mt-1">
                      عرض المواد ({(c.product_ids || []).length})
                    </button>
                  </div>
                </div>
                <div className="border-t border-[#E4EBE6] p-1.5 flex justify-end gap-1">
                  <button onClick={() => setEditing(c)} data-testid={`pc-edit-${c.id}`}
                    className="bg-[#3B82F6] hover:bg-[#2563eb] text-white text-[11px] font-bold py-1 px-2.5 rounded-full flex items-center gap-1">
                    <Edit2 className="w-3 h-3" /> تعديل
                  </button>
                  <button onClick={() => removeCompany(c)} data-testid={`pc-del-${c.id}`}
                    className="bg-[#FEE2E2] hover:bg-[#FECACA] text-[#E85D45] text-[11px] font-bold py-1 px-2 rounded-full flex items-center">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <CompanyFormModal company={editing === "new" ? null : editing} allProducts={products}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {viewing && <CompanyDetail key={viewing.id} company={viewing} allProducts={products} onClose={() => setViewing(null)} onChanged={load} />}
      <Lightbox src={zoom} onClose={() => setZoom(null)} />
    </div>
  );
};

export default PurchaseCompaniesTab;
